import { FormatComboTierSpendLevelOptions } from './types/format-combo-tier-spend-level';
import { SpendLevel, TierFieldNames } from './types/spend-level';
import {
  AbsoluteDollars,
  AbsoluteUnits,
  DollarMarketShare,
  UnitMarketShare,
  ValueLabels,
} from './types/spend-level.enum';
import { formatPlannedSpendLevel } from './format-planned-spend-level';
import { Formatter } from './formatter';

export interface DataPoint {
  value: number;
  target: number;
}

/**
 * @description A filter with a ridiculous number of options - because business likes its UI like it likes its pizza.
 * @NOTE: other than the actual number values - all other appended pieces are optional
 * A few output examples
 * @example 120230k & 20000k
 * @example 120.2k & 20k
 * @example 10% Spend MS & 20k Unit Vol
 * @example 10% Spend Market Share & 20k Unit Volume
 * @example 10% Spend Market Share & 20k Unit Volume - My Tier Name
 * @example Tier 0 - 10% Spend Market Share & 20k Unit Volume - My Tier Name
 *
 * @param spendLevel - spendLevel object - generally one provided by a tier object
 * @param formattingOptions [FormatComboTierSpendLevelOptions]
 * @param formattingOptions.truncateValues -  if true - truncate long values e.g. $150,200 = $150.2k
 * @param formattingOptions.useZeroValueLabel - if true - in certain cases we use a label instead of the spend value - e.g. $0 = Access
 * @param formattingOptions.tierName Tier name provided by backend
 * @param formattingOptions.appendTierName - if true - append tier name provided by backend to the end of this generated combo tier label
 * @param formattingOptions.sizeOfSpendTypeLabel - values are a string - "short" or "long"
 * @param formattingOptions.order - generally derived from tier.order
 *
 * The following are low level internal params that are provided by the framework specific wrapper.
 * @param currencyFilter - Internal param - passed as param to keep this transform function framework agnostic
 * @param percentFilter - Internal param -passed as param to keep this transform function framework agnostic
 * @param numberInnerFilter - Internal param - passed as param to keep this transform function framework agnostic
 *
 * @note: See function - transform()
 * This is the core code of the filter. Each framework has it's own wrapper that exports the actual filter.
 * The options are mostly the same except for the internal params that should be omitted.
 */

const SpendLevelTypesMap = {
  absoluteDollars: AbsoluteDollars,
  absoluteUnits: AbsoluteUnits,
  dollarMarketShare: DollarMarketShare,
  unitMarketShare: UnitMarketShare,
};

// part of our hacky temporary fix - see @NOTE:temporary-fix
export const DefaultTierNameFormattingOptions = {
  truncateValues: false,
  useZeroValueLabel: true,
  isSpendValue: false,
};

// TODO: put this back inside the transform exported function when back-end implements
//  null value for tier.name when tier.name is not custom value.
/**
 * @description: Combine tier values into a single string by iterating through fieldNames array
 * @NOTE:temporary-fix - hate to muddy up the code but ....
 * had to extract this function because it's part of a hacky
 * fix until the back-end defaults tier.name to null unless it is actually a custom tier name.
 * For now we have to compare tier.name against a value we generate in the front-end with
 * some very specific formattingOptions (DefaultTierNameFormattingOptions).
 */
const comboTiersValueReducer = (
  spendLevel: SpendLevel,
  formattingOptions: FormatComboTierSpendLevelOptions,
  currencyFilter: (arg1: number | null) => string,
  percentFilter: (arg1: number | null) => string,
  numberInnerFilter: (arg1: number | null) => string,
): string => {
  const truncateValues = formattingOptions.truncateValues || false;
  const spendLevelType = spendLevel.type ||
    spendLevel.spendLevelType || {
      name: '',
      displayName: '',
      fieldNames: [],
    };

  // The fieldNames array actually tells us which types are available for this tier.
  const fieldTypeNameList = spendLevelType.fieldNames as TierFieldNames[];

  // Operator is provided by back-end. It indicates the condition between the tiers. e.g.($80 & 90%)
  const operator = spendLevelType.operator?.toLowerCase() || '';

  return fieldTypeNameList.reduce(
    (val: string, fieldName: TierFieldNames): string => {
      const spend = spendLevel[fieldName] || 0;
      const tierGeneratedLabel = formatPlannedSpendLevel(
        roundNumbers(spend),
        fieldName,
        truncateValues,
        currencyFilter,
        percentFilter,
        numberInnerFilter,
        formattingOptions.isSpendValue,
        operator,
        formattingOptions.areRingChartNewRulesEnabled,
      );

      val += val.length ? ` ${operator} ` : ``;

      // If planned spend level is less than or equal to 0 - label "Access"
      val +=
        spend === 0 && formattingOptions.useZeroValueLabel
          ? ValueLabels.ZERO
          : tierGeneratedLabel;

      if (
        formattingOptions.isSpendValue &&
        (val.includes(ValueLabels.ZERO) || spend === 0) &&
        spendLevel.target &&
        spendLevel.target[fieldName] > 0
      ) {
        val = '--';
      }

      // Show spendTypeLabel in short or long format
      // @example SizeOfSpendTypeLabel.SHORT = 1k Spend MS
      // @example SizeOfSpendTypeLabel.LONG = 1k Spend Market Share
      if (formattingOptions.sizeOfSpendTypeLabel) {
        val += ` ${
          SpendLevelTypesMap[fieldName][formattingOptions.sizeOfSpendTypeLabel]
        }`;
      }

      return val;
    },
    '',
  );
};

/**
 * Not a pipe, not much of anything except yet another wrapper for the transform code.
 * There must be a dozen of these and if you think about it even a little bit, it could have simply been
 * canned formats (ala what you see in libraries like Moment) and we could have just passed in that we
 * wanted format 1 or format 2. But no, we have to have a wrapper for every possible combination of options.
 */
export class FormatComboTierSpendLevelPipe {
  static transform(
    spendLevel: SpendLevel,
    args?: FormatComboTierSpendLevelOptions,
  ): string {
    return transformWithFiveParams(
      spendLevel,
      args,
      Formatter.currency,
      Formatter.percent,
      (value: number | null) =>
        new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
          value as number,
        ),
    );
  }
}

/**
 * The worst name ever, for some of the worst code ever.
 */
export function transformWithFiveParams(
  spendLevel: SpendLevel = { type: { fieldNames: [] as TierFieldNames[] } },
  formattingOptions: FormatComboTierSpendLevelOptions = {
    truncateValues: false,
    useZeroValueLabel: false,
  },
  currencyFilter: (arg1: number | null) => string,
  percentFilter: (arg1: number | null) => string,
  numberInnerFilter: (arg1: number | null) => string,
) {
  return formatComboTierSpendLevel(
    spendLevel,
    formattingOptions,
    currencyFilter,
    percentFilter,
    numberInnerFilter,
  );
}

/**
 * TODO: We need to rename this function as it is not a Pipe. Something more meanful
 * such as formatComboTierSpendLevel as in target-and-spend.service.ts
 * PARTIAL SOLUTION: Swapped named transform with formatComboTierSpendLevel.
 * I use transform as a wrapper for consistency, and now we are going to start using formatComboTierSpendLevel.
 */

export function formatComboTierSpendLevel(
  spendLevel: SpendLevel = { type: { fieldNames: [] as TierFieldNames[] } },
  formattingOptions: FormatComboTierSpendLevelOptions = {
    truncateValues: false,
    useZeroValueLabel: false,
  },
  currencyFilter: (arg1: number | null) => string,
  percentFilter: (arg1: number | null) => string,
  numberInnerFilter: (arg1: number | null) => string,
): string {
  let combinedTierValues = comboTiersValueReducer(
    spendLevel,
    formattingOptions,
    currencyFilter,
    percentFilter,
    numberInnerFilter,
  );

  // TODO: remove when back-end implements null value for tier.name when tier.name is not custom value.
  // This defaultTierName is part of a hacky fix to prevent duplicate portions in the tier label because of the tier name.
  // The back-end will eventually return null for tier name if it is not custom so this garbage will eventually be able to go away.
  const defaultTierName = comboTiersValueReducer(
    spendLevel,
    DefaultTierNameFormattingOptions,
    currencyFilter,
    percentFilter,
    numberInnerFilter,
  );

  // TODO: remove when back-end implements null value for tier.name when tier.name is not custom value.
  const hasDuplicateTierName =
    defaultTierName === formattingOptions.tierName ||
    combinedTierValues === formattingOptions.tierName;

  // append tier name if appendName true and tierName has a value and (combinedTierValues or defaultTierName) are NOT equal to tierName
  combinedTierValues =
    formattingOptions.appendTierName &&
    formattingOptions.tierName &&
    !hasDuplicateTierName
      ? `${combinedTierValues} - ${formattingOptions.tierName}`
      : combinedTierValues;

  // prepend private label for private tier

  if (formattingOptions.isPrivate) {
    combinedTierValues = `Private ${
      formattingOptions.hidePrivateSeparator ? '' : '- '
    }${combinedTierValues}`;
  }
  // ugh... prepend Tier # - if param is provided
  if (formattingOptions.order != null) {
    combinedTierValues = `Tier ${formattingOptions.order} - ${combinedTierValues}`;
  }

  // Vizient contracts have cases where tier values are not defined so we default to showing the tierName that is already provided.
  return combinedTierValues || formattingOptions.tierName || '';
}

export function transformSpendLevelToDataPoint(offer: any): DataPoint {
  return offer.selectedTier?.spendLevelType?.fieldNames.map(
    (fieldName: string) => {
      let value = 0;
      let target = 0;

      switch (fieldName) {
        case 'dollarMarketShare':
          value = offer.allocatedMarketShare;
          target = offer.selectedTier.dollarMarketShare;
          break;
        case 'unitMarketShare':
          value = offer.allocatedUnitMarketShare;
          target = offer.selectedTier.unitMarketShare;
          break;
        case 'absoluteDollars':
          value = offer.total;
          target = offer.selectedTier.absoluteDollars;
          break;
        case 'absoluteUnits':
          value = offer.plannedUnits;
          target = offer.selectedTier.absoluteUnits;
          break;
      }

      return {
        value: value / 100,
        target: target / 100,
      };
    },
  );
}

export function isValueDashable(items: DataPoint[]): boolean {
  return items.some((item) => item.value === 0 && item.target > 0);
}


export function roundNumbers(data: number): number {
  return Math.round(data);
}
